(() => {
  const element = document.getElementById('visbiome-guarantee-config');
  if (!element || window.VisbiomeGuarantee) return;
  const config = JSON.parse(element.textContent);
  const root = config.root.replace(/\/?$/, '/');
  const originalFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  let backgroundPending = false;
  let resumeForm = null;

  function enqueue(task) {
    const result = queue.then(task, task);
    queue = result.catch(() => {});
    return result;
  }

  async function request(url, options = {}) {
    const response = await originalFetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000), ...options });
    if (!response.ok) throw new Error(`Guarantee request failed (${response.status})`);
    return response;
  }

  async function readState() {
    const url = new URL(`${root}cart`, window.location.origin);
    url.searchParams.set('section_id', config.stateSection);
    const response = await request(url, { cache: 'no-store' });
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const state = html.querySelector('[data-visbiome-guarantee-state]');
    if (!state) throw new Error('Guarantee cart state is unavailable');
    const parsed = JSON.parse(state.textContent);
    if (!Array.isArray(parsed.cart?.items)) throw new Error('Invalid guarantee cart state');
    return parsed;
  }

  async function post(path, body) {
    const response = await request(`${root}cart/${path}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (result.status || result.errors) throw new Error('Guarantee cart update was rejected');
    return result;
  }

  async function reconcile() {
    let state = await readState();
    const lines = state.cart.items.filter(item => String(item.product_id) === String(config.productId));
    const valid = state.usable && String(state.variantId) === String(config.variantId);
    const needsGuarantee = valid && state.eligible;
    const retained = needsGuarantee
      ? lines.find(item => String(item.variant_id || item.id) === String(config.variantId) && !item.selling_plan_allocation)
      : null;
    const updates = {};
    for (const item of lines) {
      const target = item === retained ? 1 : 0;
      if (item.quantity !== target) updates[item.key] = target;
    }
    let changed = false;
    if (Object.keys(updates).length) {
      await post('update', { updates });
      changed = true;
    }
    if (needsGuarantee && !retained) {
      await post('add', {
        items: [{ id: config.variantId, quantity: 1, properties: { _visbiome_guarantee: 'included', _guarantee_policy: '/pages/money-back' } }]
      });
      changed = true;
    }
    if (changed) state = await readState();
    return { changed, cart: state.cart };
  }

  async function fetchSections(ids, path = window.location.pathname) {
    const url = new URL(path, window.location.origin);
    url.search = '';
    url.searchParams.set('sections', ids.slice(0, 5).join(','));
    return (await request(url, { cache: 'no-store' })).json();
  }

  async function refreshVisibleCart() {
    const main = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const ids = main
      ? [main.dataset.id, footer?.dataset.id, 'cart-icon-bubble'].filter(Boolean)
      : ['cart-drawer', 'cart-icon-bubble'];
    const sections = await fetchSections(ids);
    for (const id of ids) {
      if (!sections[id]) continue;
      const html = new DOMParser().parseFromString(sections[id], 'text/html');
      if (id === 'cart-icon-bubble') {
        const target = document.getElementById(id);
        const source = html.querySelector('.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      } else if (id === 'cart-drawer') {
        const drawer = document.querySelector('cart-drawer');
        const target = document.getElementById('CartDrawer');
        const source = html.getElementById('CartDrawer');
        if (target && source) {
          target.innerHTML = source.innerHTML;
          const sourceDrawer = html.querySelector('cart-drawer');
          drawer?.classList.toggle('is-empty', sourceDrawer?.classList.contains('is-empty'));
          target.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
        }
      } else {
        const target = document.getElementById(`shopify-section-${id}`);
        const source = html.getElementById(`shopify-section-${id}`);
        if (target && source) target.innerHTML = source.innerHTML;
      }
    }
  }

  function report(error) {
    console.warn('[Visbiome guarantee]', error.message);
    document.dispatchEvent(new CustomEvent('visbiome-guarantee:error', { detail: { message: error.message } }));
  }

  window.fetch = function(input, options) {
    let url;
    try { url = new URL(input instanceof Request ? input.url : String(input), window.location.origin); }
    catch { return originalFetch(input, options); }
    const method = (options?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const mutation = url.origin === window.location.origin && /\/cart\/(add|change|update|clear)\.js$/.test(url.pathname) && method === 'POST';
    if (!mutation) return originalFetch(input, options);
    return enqueue(async () => {
      const response = await originalFetch(input, options);
      if (!response.ok) return response;
      let payload;
      try { payload = await response.clone().json(); }
      catch { return response; }
      if (payload.status || payload.errors) return response;
      try {
        const result = await reconcile();
        if (!result.changed) return response;
        let sections = payload.sections;
        if (sections) sections = await fetchSections(Object.keys(sections));
        const body = /\/add\.js$/.test(url.pathname)
          ? { ...payload, ...(sections ? { sections } : {}) }
          : { ...payload, ...result.cart, ...(sections ? { sections } : {}) };
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        headers.set('content-type', 'application/json');
        return new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        report(error);
        return response;
      }
    });
  };

  function sync() {
    if (backgroundPending) return queue;
    backgroundPending = true;
    return enqueue(async () => {
      try {
        const result = await reconcile();
        if (result.changed) await refreshVisibleCart();
        return result;
      } catch (error) {
        report(error);
      } finally {
        backgroundPending = false;
      }
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || !submitter || submitter.name !== 'checkout') return;
    if (resumeForm === form) { resumeForm = null; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueue(async () => {
      try { await reconcile(); }
      catch (error) { report(error); }
      resumeForm = form;
      form.requestSubmit(submitter);
    });
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = new URL(link.href, window.location.origin);
    if (target.origin !== window.location.origin || !/\/checkout\/?$/.test(target.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueue(async () => {
      try { await reconcile(); }
      catch (error) { report(error); }
      window.location.assign(link.href);
    });
  }, true);

  window.VisbiomeGuarantee = { sync };
  window.addEventListener('pageshow', event => { if (event.persisted) sync(); });
  document.addEventListener('cart:is-updated', sync);
  document.addEventListener('cart-drawer:contents-rendered', sync);
  sync();
})();
